import {
  Flex,
  Icon,
  IconButton,
  Link,
  Select,
  SelectOption,
  Text,
} from '@driftt/dds'
import { faPlus } from '@driftt/dds.icons/regular/faPlus'
import { faXmark } from '@driftt/dds.icons/solid/faXmark'
import { uniq } from 'ramda'
import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import {
  AVAILABLE_OPERATORS,
  CREATE_ATTRIBUTE_OPTIONS,
  OPERATORS_LIST,
} from 'components/AIConfiguration/LeadQualification/constants'
import { useAttributeValueFactory } from 'components/AIConfiguration/LeadQualification/PersonaCard/hooks'
import { PersonaForm } from 'components/AIConfiguration/LeadQualification/PersonaCard/types'
import { isEmptyOrNil } from 'utils/index'

import { AttributeOption } from '../../types'

interface Props {
  idx: number
  disabled: boolean
  remove: (idx: number) => void
  canDelete: boolean
  attributeNameOptions:
    | {
        label: string
        value: string
      }[]
    | {
        label: string
        options: {
          label: string
          value: string
        }[]
      }[]
}

const AttributeRow = ({
  idx,
  disabled,
  remove,
  canDelete,
  attributeNameOptions,
}: Props) => {
  const {
    watch,
    control,
    setValue,
    formState: { errors: { attributes: attributeErrors = {} } = {} },
  } = useFormContext<PersonaForm>()

  const operator = watch(`attributes.${idx}.operator`)
  const name = watch(`attributes.${idx}.name`) || {}
  const { type = '', options } = name as any // eslint-disable-line @typescript-eslint/no-explicit-any
  const formattedOptions =
    options &&
    options.length >= 0 &&
    uniq(options).map((option) => ({ value: option, label: option }))

  const { component, validation, defaultValue } = useAttributeValueFactory(
    operator?.value
  )

  const FILTERED_OPERATORS_LIST =
    type === 'ENUMARRAY'
      ? OPERATORS_LIST.filter(
          (item) => item.value === 'in' || item.value === 'equals'
        )
      : OPERATORS_LIST

  const hideInput = operator?.value === AVAILABLE_OPERATORS.EXISTS

  const handleAttributeChange = (
    attr: AttributeOption,
    onChange: (option: { label: string; value: string }) => void
  ) => {
    if (!isEmptyOrNil(attr?.href)) {
      window.location.pathname = attr?.href
      return
    }
    onChange(attr as { label: string; value: string })
  }

  return (
    <Flex gap={1}>
      <Controller
        name={`attributes.${idx}.name`}
        control={control}
        rules={{ required: 'Attribute required' }}
        render={({ field: { value, onChange } }) => (
          <Select
            label="Attribute"
            noOptionsMessage={() => (
              <Flex direction="column" fullWidth>
                {CREATE_ATTRIBUTE_OPTIONS.map(({ href, label }) => (
                  <Link href={href}>
                    <Flex
                      direction="row"
                      alignItems="center"
                      justifyContent="flex-start"
                      fullWidth
                    >
                      <Icon icon={faPlus} css={{ color: '$black' }} />
                      <Text>{label}</Text>
                    </Flex>
                  </Link>
                ))}
              </Flex>
            )}
            options={
              [
                ...CREATE_ATTRIBUTE_OPTIONS,
                ...attributeNameOptions,
              ] as SelectOption[]
            }
            onChange={(option) =>
              handleAttributeChange(option as AttributeOption, onChange)
            }
            value={
              typeof value === 'string'
                ? {
                    label: value,
                    value,
                  }
                : value
            }
            isDisabled={disabled}
            error={attributeErrors[idx]?.name?.message}
          />
        )}
      />

      <Controller
        name={`attributes.${idx}.operator`}
        control={control}
        rules={{ required: 'Operator required' }}
        render={({ field: { value, onChange } }) => (
          <div title={value?.label}>
            <Select
              size="small"
              label="Condition"
              options={FILTERED_OPERATORS_LIST}
              onChange={(value) => {
                setValue(`attributes.${idx}.value`, '', {
                  shouldValidate: true,
                })
                onChange(value)
              }}
              value={value}
              isDisabled={disabled}
              error={attributeErrors[idx]?.operator?.message}
            />
          </div>
        )}
      />

      {!hideInput && (
        <Controller
          name={`attributes.${idx}.value`}
          control={control}
          rules={validation}
          render={({ field: { value, onChange } }) => {
            return component({
              disabled,
              onChange,
              error: attributeErrors[idx]?.value?.message,
              value: Boolean(value?.length) ? value : defaultValue,
              options: formattedOptions,
            })
          }}
        />
      )}

      {canDelete && (
        <IconButton
          iconVariant="gray"
          css={{
            marginTop: '24px',
          }}
          title="remove attribute"
          icon={faXmark}
          onClick={() => remove(idx)}
          disabled={disabled}
        />
      )}
    </Flex>
  )
}

export default AttributeRow
